import { useEffect, useMemo, useState } from 'react'
import { useWorkspace } from '../data/workspace'
import { subscribeBlocksInRange } from '../data/blocks'
import { iterationDays, iterationsInRange, type Iteration } from '../data/iterations'
import {
  categoryBreakdown,
  countedBlocks,
  excludedCategories,
  fragmentation,
  timeByFlag,
  workBalance,
} from '../data/insights'
import { IterationsEditor } from '../ui/IterationsEditor'
import { Donut, type DonutSlice } from '../ui/Donut'
import { Screen } from '../ui/Screen'
import { formatDayLabel, formatDuration } from '../lib/format'
import { dayRange, shiftDayKey, todayKey } from '../lib/time'
import type { Block, Category } from '../data/types'

export function IterationsPage() {
  const { uid, categories, settings, iterations, taxonomy } = useWorkspace()
  const [open, setOpen] = useState<string | null>(null)
  const [scope, setScope] = useState<string | null>(null)
  const [showSettings, setShowSettings] = useState(false)
  const [blocks, setBlocks] = useState<Block[]>([])

  const today = todayKey(settings.dayStartHour)
  const list = useMemo(
    () => iterationsInRange(iterations, shiftDayKey(today, -365), today).reverse(),
    [iterations, today],
  )

  const span = useMemo(() => {
    const earliest = list[list.length - 1]?.startDay ?? today
    const latest = list[0]?.endDay ?? today
    return {
      start: dayRange(earliest, settings.dayStartHour).start,
      end: dayRange(latest < today ? latest : today, settings.dayStartHour).end,
    }
  }, [list, today, settings.dayStartHour])

  useEffect(
    () => subscribeBlocksInRange(uid, span.start, span.end, setBlocks),
    [uid, span.start, span.end],
  )

  // The newest iteration opens by default: it is the one still in progress.
  useEffect(() => {
    if (open === null && list[0]) setOpen(list[0].id)
  }, [list, open])

  return (
    <Screen wide scroll>
      <div className="flex flex-wrap items-center gap-2 pb-3">
        <h2 className="text-[14px] font-semibold">Iterations</h2>
        <ScopePicker flags={taxonomy?.flags ?? []} scope={scope} onChange={setScope} />
        <button
          type="button"
          onClick={() => setShowSettings((current) => !current)}
          aria-expanded={showSettings}
          className="ml-auto rounded-md border border-line px-2 py-1 text-[13px] font-medium text-dim transition-colors duration-150 hover:border-line-strong hover:text-text"
        >
          {showSettings ? 'Done' : 'Set them up'}
        </button>
      </div>

      {showSettings ? (
        <div className="mb-3 overflow-hidden rounded-xl border border-line bg-surface">
          <IterationsEditor uid={uid} config={iterations} dayStartHour={settings.dayStartHour} />
        </div>
      ) : null}

      {list.length === 0 ? (
        <p className="rounded-xl border border-line bg-surface px-3 py-6 text-center text-[13px] leading-relaxed text-faint">
          No iterations yet. Set a cadence — a name, a start day, a length — and every iteration
          from then on is generated for you.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {list.map((iteration) => (
            <IterationRow
              key={iteration.id}
              iteration={iteration}
              blocks={blocks}
              categories={categories}
              flags={taxonomy?.flags ?? []}
              settings={settings}
              today={today}
              scope={scope}
              expanded={open === iteration.id}
              onToggle={() => setOpen(open === iteration.id ? null : iteration.id)}
            />
          ))}
        </ul>
      )}
    </Screen>
  )
}

interface RowProps {
  iteration: Iteration
  blocks: Block[]
  categories: Map<string, Category>
  flags: Array<{ id: string; label: string }>
  settings: {
    dayStartHour: number
    workdayStartHour: number
    workdayEndHour: number
    hideWeekends: boolean
    dailyTargetHours: number
  }
  today: string
  scope: string | null
  expanded: boolean
  onToggle: () => void
}

function IterationRow({
  iteration,
  blocks,
  categories,
  flags,
  settings,
  today,
  scope,
  expanded,
  onToggle,
}: RowProps) {
  // An iteration that has not finished yet reports only the part that has.
  const days = iterationDays(iteration).filter((day) => day <= today)
  const running = iteration.endDay >= today

  const range = useMemo(() => {
    if (days.length === 0) return null
    return {
      from: dayRange(days[0] as string, settings.dayStartHour).start.getTime(),
      to: dayRange(days[days.length - 1] as string, settings.dayStartHour).end.getTime(),
    }
  }, [days, settings.dayStartHour])

  const inRange = useMemo(
    () =>
      range
        ? blocks.filter(
            (block) => block.start < range.to && (block.end ?? Number.POSITIVE_INFINITY) > range.from,
          )
        : [],
    [blocks, range],
  )

  // Same rule as Insights: what a flag has been marked out of never gets here.
  const counted = useMemo(
    () => countedBlocks(inRange, excludedCategories(categories)),
    [inRange, categories, flags],
  )
  const breakdown = categoryBreakdown(counted, categories)
  const tracked = breakdown.reduce((sum, entry) => sum + entry.ms, 0)
  const frag = fragmentation(counted, categories, scope)
  const balance = workBalance(counted, days, settings, today)
  const byFlag = timeByFlag(counted, categories)

  const slices: DonutSlice[] = breakdown.map((entry) => ({
    id: entry.categoryId,
    label: categories.get(entry.categoryId)?.label ?? entry.categoryId,
    value: entry.ms,
    color: categories.get(entry.categoryId)?.color ?? 'var(--tg-line-strong)',
  }))

  return (
    <li className="overflow-hidden rounded-xl border border-line bg-surface">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors duration-150 hover:bg-raised/50"
      >
        <span
          aria-hidden="true"
          className={`text-faint transition-transform duration-150 ${expanded ? 'rotate-90' : ''}`}
        >
          ›
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[14px] font-medium text-text">
            {iteration.name}
            {running ? <span className="pl-2 text-[11px] text-faint uppercase">in progress</span> : null}
          </span>
          <span className="block text-[12px] text-faint tnum">
            {formatDayLabel(iteration.startDay, 'd MMM')} – {formatDayLabel(iteration.endDay, 'd MMM')}
          </span>
        </span>
        <span className="shrink-0 text-right">
          <span className="block text-[14px] font-semibold tnum">{formatDuration(tracked)}</span>
          <span className="block text-[11px] text-faint">tracked</span>
        </span>
      </button>

      {expanded ? (
        <div className="flex flex-col gap-4 border-t border-line px-3 py-3">
          <div className="flex flex-wrap items-end gap-x-8 gap-y-3">
            <Figure value={formatDuration(frag.median)} label={`median ${scopeLabel(scope, flags)} stretch`} />
            <Figure value={String(frag.durations.length)} label="stretches" />
            <Figure value={formatDuration(frag.total)} label={scopeLabel(scope, flags)} />
            <Figure
              value={`${balance.balance >= 0 ? '+' : '−'}${formatDuration(Math.abs(balance.balance))}`}
              label={balance.balance >= 0 ? 'banked' : 'owed'}
            />
          </div>

          <Donut slices={slices} centre={formatDuration(tracked)} />

          {byFlag.slices.length > 0 ? (
            <div>
              <h3 className="pb-1.5 text-[12px] font-semibold tracking-wide text-faint uppercase">
                Time by flag
              </h3>
              <ul className="flex flex-col gap-1">
                {byFlag.slices.map((slice) => (
                  <li key={slice.flagId ?? 'untagged'} className="flex items-baseline gap-2 text-[12px]">
                    <span className="min-w-0 flex-1 truncate text-dim">
                      {slice.flagId
                        ? (flags.find((flag) => flag.id === slice.flagId)?.label ?? slice.flagId)
                        : 'no flag'}
                    </span>
                    <span className="shrink-0 text-faint tnum">{formatDuration(slice.ms)}</span>
                    <span className="w-9 shrink-0 text-right text-faint tnum">
                      {Math.round(slice.share * 100)}%
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}
    </li>
  )
}

function Figure({ value, label }: { value: string; label: string }) {
  return (
    <div>
      <p className="text-[20px] leading-none font-semibold tracking-tight tnum">{value}</p>
      <p className="pt-1 text-[12px] leading-tight text-faint">{label}</p>
    </div>
  )
}

/** Which flag the fragmentation figures are about. Nothing selected means all tracked time. */
export function ScopePicker({
  flags,
  scope,
  onChange,
}: {
  flags: Array<{ id: string; label: string }>
  scope: string | null
  onChange: (scope: string | null) => void
}) {
  if (flags.length === 0) return null
  return (
    <div className="flex flex-wrap rounded-lg border border-line p-0.5">
      <ScopeButton active={scope === null} onClick={() => onChange(null)}>
        All tracked
      </ScopeButton>
      {flags.map((flag) => (
        <ScopeButton key={flag.id} active={scope === flag.id} onClick={() => onChange(flag.id)}>
          {flag.label}
        </ScopeButton>
      ))}
    </div>
  )
}

function ScopeButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`rounded-md px-2.5 py-1 text-[13px] font-medium transition-colors duration-150 ${
        active ? 'bg-raised text-text' : 'text-dim hover:text-text'
      }`}
    >
      {children}
    </button>
  )
}

function scopeLabel(scope: string | null, flags: Array<{ id: string; label: string }>): string {
  if (scope === null) return 'tracked'
  return flags.find((flag) => flag.id === scope)?.label ?? scope
}
